import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Sparkles, BookOpen, GraduationCap, Users, UserPlus,
  ArrowRight, ArrowLeft, Check, Loader2, Heart, Calculator,
  Microscope, Pencil, Hash, School
} from 'lucide-react';
import RatioSelector from '@/components/RatioSelector';

const OrganizationOnboarding = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billingConfirmed, setBillingConfirmed] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  
  // Step 1: Curriculum Selection
  const [curriculum, setCurriculum] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  
  // Step 2: Classes
  const [classes, setClasses] = useState([{ name: '', grade_level: '', subject_focus: '', small_setting_ratio: '' }]);
  
  // Step 3: Teachers
  const [teachers, setTeachers] = useState([{ name: '', email: '', class_ids: [] }]);
  const [createdClasses, setCreatedClasses] = useState([]);

  const subjects = [
    { id: 'reading', name: 'Reading/ELA (EL Education)', icon: BookOpen, color: 'bg-blue-100 text-blue-600' },
    { id: 'math', name: 'Math (Envision)', icon: Calculator, color: 'bg-green-100 text-green-600' },
    { id: 'alphabets', name: 'Alphabets', icon: Pencil, color: 'bg-purple-100 text-purple-600' },
    { id: 'counting', name: 'Counting', icon: Hash, color: 'bg-orange-100 text-orange-600' },
    { id: 'writing', name: 'Writing', icon: Pencil, color: 'bg-pink-100 text-pink-600' },
    { id: 'science', name: 'Science', icon: Microscope, color: 'bg-teal-100 text-teal-600' },
    { id: 'christian', name: 'Christian/Bible', icon: Heart, color: 'bg-amber-100 text-amber-600' },
  ];

  const grades = ['Pre-K', 'Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade', '6th Grade'];

  useEffect(() => {
    fetchOnboardingStatus();
    // If arriving from Stripe success, confirm the payment and flag billing as ready.
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    if (sessionId) {
      (async () => {
        try {
          const r = await api.get(`/subscriptions/status/${sessionId}`);
          if (r.data?.payment_status === 'paid' || r.data?.status === 'complete') {
            setBillingConfirmed(true);
            toast.success('Billing enabled — let\'s set up your classes and teachers.');
          } else {
            toast.info('Payment pending — you can finish setup and we\'ll retry billing later.');
          }
        } catch {
          toast.info('Continuing setup — you can finish billing from Organization Settings anytime.');
        }
      })();
    } else if (cancelled) {
      toast.info('Checkout cancelled — you can restart billing from Organization Settings anytime.');
    }
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await api.get('/organization/onboarding/status');
      setOnboardingStatus(response.data);
      if (response.data.onboarding_completed) {
        navigate('/organization');
      } else if (response.data.current_step > 0) {
        setStep(response.data.current_step + 1);
        setCurriculum(response.data.curriculum || []);
        setGradeLevels(response.data.grade_levels || []);
      }
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    }
  };

  const toggleCurriculum = (subjectId) => {
    setCurriculum(prev => 
      prev.includes(subjectId) 
        ? prev.filter(s => s !== subjectId)
        : [...prev, subjectId]
    );
  };

  const toggleGrade = (grade) => {
    setGradeLevels(prev =>
      prev.includes(grade)
        ? prev.filter(g => g !== grade)
        : [...prev, grade]
    );
  };

  const handleStep1Submit = async () => {
    if (curriculum.length === 0) {
      toast.error('Please select at least one subject');
      return;
    }
    if (gradeLevels.length === 0) {
      toast.error('Please select at least one grade level');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/organization/onboarding/step1', {
        curriculum_selection: curriculum,
        grade_levels: gradeLevels
      });
      toast.success('Curriculum configured!');
      setStep(2);
    } catch (error) {
      toast.error('Failed to save curriculum settings');
    } finally {
      setLoading(false);
    }
  };

  const addClass = () => {
    setClasses([...classes, { name: '', grade_level: '', subject_focus: '', small_setting_ratio: '' }]);
  };

  const updateClass = (index, field, value) => {
    const newClasses = [...classes];
    newClasses[index][field] = value;
    setClasses(newClasses);
  };

  const removeClass = (index) => {
    if (classes.length > 1) {
      setClasses(classes.filter((_, i) => i !== index));
    }
  };

  const handleStep2Submit = async () => {
    const validClasses = classes.filter(c => c.name.trim());
    if (validClasses.length === 0) {
      toast.error('Please create at least one class');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/organization/onboarding/step2', {
        classes: validClasses
      });
      setCreatedClasses(response.data.classes);
      toast.success(`${response.data.classes.length} classes created!`);
      setStep(3);
    } catch (error) {
      toast.error('Failed to create classes');
    } finally {
      setLoading(false);
    }
  };

  const addTeacher = () => {
    setTeachers([...teachers, { name: '', email: '', class_ids: [] }]);
  };

  const updateTeacher = (index, field, value) => {
    const newTeachers = [...teachers];
    newTeachers[index][field] = value;
    setTeachers(newTeachers);
  };

  const toggleTeacherClass = (teacherIndex, classId) => {
    const newTeachers = [...teachers];
    const classIds = newTeachers[teacherIndex].class_ids || [];
    if (classIds.includes(classId)) {
      newTeachers[teacherIndex].class_ids = classIds.filter(id => id !== classId);
    } else {
      newTeachers[teacherIndex].class_ids = [...classIds, classId];
    }
    setTeachers(newTeachers);
  };

  const removeTeacher = (index) => {
    if (teachers.length > 1) {
      setTeachers(teachers.filter((_, i) => i !== index));
    }
  };

  const handleStep3Submit = async () => {
    const validTeachers = teachers.filter(t => t.name.trim() && t.email.trim());
    
    setLoading(true);
    try {
      if (validTeachers.length > 0) {
        const response = await api.post('/organization/onboarding/step3', {
          teachers: validTeachers
        });
        
        // Show temporary passwords
        if (response.data.teachers.length > 0) {
          toast.success(
            <div>
              <p className="font-semibold">Teachers invited!</p>
              {response.data.teachers.map(t => (
                <p key={t.id} className="text-sm">
                  {t.email}: {t.temp_password}
                </p>
              ))}
            </div>,
            { duration: 10000 }
          );
        }
      }
      
      // Complete onboarding
      await api.post('/organization/onboarding/complete');
      toast.success('Onboarding complete!');
      navigate('/organization');
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      console.error('Onboarding step3/complete failed:', error?.response?.data || error);
      toast.error(`Failed to complete onboarding: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const skipTeachers = async () => {
    setLoading(true);
    try {
      await api.post('/organization/onboarding/complete');
      toast.success('Onboarding complete!');
      navigate('/organization');
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      console.error('Onboarding complete failed:', error?.response?.data || error);
      toast.error(`Failed to complete onboarding: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header */}
      <header className="p-4 border-b bg-white">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-xl text-slate-800">Organization Setup</h1>
            <p className="text-sm text-slate-500">Step {step} of 3</p>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        {billingConfirmed && (
          <div
            className="mb-6 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 flex items-start gap-3"
            data-testid="onboarding-billing-confirmed"
          >
            <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-fredoka text-lg font-bold text-slate-900">Billing is set up</p>
              <p className="text-sm text-slate-600">
                Great — payment received. Continue below to create your classes and invite your teachers.
              </p>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                s < step ? 'bg-green-500 text-white' :
                s === step ? 'bg-purple-600 text-white' :
                'bg-slate-200 text-slate-500'
              }`}>
                {s < step ? <Check className="w-5 h-5" /> : s}
              </div>
              <span className={`ml-2 hidden sm:block ${s === step ? 'text-purple-600 font-medium' : 'text-slate-500'}`}>
                {s === 1 ? 'Curriculum' : s === 2 ? 'Classes' : 'Teachers'}
              </span>
              {s < 3 && <div className={`w-16 sm:w-24 h-1 mx-4 ${s < step ? 'bg-green-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <main className="max-w-4xl mx-auto px-4 pb-12">
        {/* Step 1: Curriculum Selection */}
        {step === 1 && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <BookOpen className="w-6 h-6 text-purple-500" />
                Configure Curriculum
              </CardTitle>
              <CardDescription>
                Select the subjects and grade levels your organization will use. 
                Reading uses EL Education and Math uses Envision curriculum.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Subjects */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Select Subjects</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {subjects.map(subject => (
                    <div
                      key={subject.id}
                      onClick={() => toggleCurriculum(subject.id)}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        curriculum.includes(subject.id)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 hover:border-purple-300'
                      }`}
                      data-testid={`subject-${subject.id}`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${subject.color}`}>
                        <subject.icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{subject.name}</p>
                        {(subject.id === 'reading' || subject.id === 'math') && (
                          <p className="text-xs text-slate-500">Rigorous curriculum</p>
                        )}
                      </div>
                      <Checkbox checked={curriculum.includes(subject.id)} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Grade Levels */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Grade Levels Served</h3>
                <div className="flex flex-wrap gap-2">
                  {grades.map(grade => (
                    <Button
                      key={grade}
                      variant={gradeLevels.includes(grade) ? 'default' : 'outline'}
                      onClick={() => toggleGrade(grade)}
                      className={gradeLevels.includes(grade) ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      data-testid={`grade-${grade}`}
                    >
                      {grade}
                    </Button>
                  ))}
                </div>
              </div>

              <Button
                onClick={handleStep1Submit}
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg"
                data-testid="step1-next-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                Continue to Classes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Create Classes */}
        {step === 2 && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <School className="w-6 h-6 text-green-500" />
                Create Classes
              </CardTitle>
              <CardDescription>
                Create classes or groups for your organization. You can name them however you like 
                (e.g., "Morning Stars", "Grade 2 Math", "Sunday School K-2").
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {classes.map((cls, index) => (
                <div key={index} className="p-4 bg-slate-50 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Class {index + 1}</h4>
                    {classes.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeClass(index)}>
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Class Name *</Label>
                      <Input
                        placeholder="e.g., Morning Stars"
                        value={cls.name}
                        onChange={(e) => updateClass(index, 'name', e.target.value)}
                        data-testid={`class-name-${index}`}
                      />
                    </div>
                    <div>
                      <Label>Grade Level (Optional)</Label>
                      <Select
                        value={cls.grade_level}
                        onValueChange={(v) => updateClass(index, 'grade_level', v)}
                      >
                        <SelectTrigger data-testid={`class-grade-${index}`}>
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                        <SelectContent>
                          {grades.map(g => (
                            <SelectItem key={g} value={g}>{g}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Subject Focus (Optional)</Label>
                      <Select
                        value={cls.subject_focus}
                        onValueChange={(v) => updateClass(index, 'subject_focus', v)}
                      >
                        <SelectTrigger data-testid={`class-subject-${index}`}>
                          <SelectValue placeholder="All subjects" />
                        </SelectTrigger>
                        <SelectContent>
                          {subjects.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <RatioSelector
                      value={cls.small_setting_ratio || ''}
                      onChange={(v) => updateClass(index, 'small_setting_ratio', v)}
                      label="Small-setting ratio (optional)"
                      dataTestid={`class-ratio-${index}`}
                    />
                  </div>
                </div>
              ))}

              <Button variant="outline" onClick={addClass} className="w-full" data-testid="add-class-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Another Class
              </Button>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleStep2Submit}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="step2-next-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  Continue to Teachers
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Invite Teachers */}
        {step === 3 && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Users className="w-6 h-6 text-blue-500" />
                Invite Teachers
              </CardTitle>
              <CardDescription>
                Add teachers to your organization. They will only be able to view and manage 
                students in their assigned classes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {teachers.map((teacher, index) => (
                <div key={index} className="p-4 bg-slate-50 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Teacher {index + 1}</h4>
                    {teachers.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeTeacher(index)}>
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        placeholder="Teacher Name"
                        value={teacher.name}
                        onChange={(e) => updateTeacher(index, 'name', e.target.value)}
                        data-testid={`teacher-name-${index}`}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="teacher@school.com"
                        value={teacher.email}
                        onChange={(e) => updateTeacher(index, 'email', e.target.value)}
                        data-testid={`teacher-email-${index}`}
                      />
                    </div>
                  </div>
                  
                  {createdClasses.length > 0 && (
                    <div>
                      <Label>Assign to Classes</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {createdClasses.map(cls => (
                          <Button
                            key={cls.id}
                            size="sm"
                            variant={(teacher.class_ids || []).includes(cls.id) ? 'default' : 'outline'}
                            onClick={() => toggleTeacherClass(index, cls.id)}
                            className={(teacher.class_ids || []).includes(cls.id) ? 'bg-blue-600' : ''}
                          >
                            {cls.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <Button variant="outline" onClick={addTeacher} className="w-full" data-testid="add-teacher-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Another Teacher
              </Button>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button variant="ghost" onClick={skipTeachers} className="flex-1">
                  Skip for Now
                </Button>
                <Button
                  onClick={handleStep3Submit}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="step3-complete-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  Complete Setup
                  <Check className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default OrganizationOnboarding;
